<template>
  <section class="hero">
    <div class="container">
      <div class="hero__slider">
        <div
          class="hero__track"
          :style="{ transform: `translateX(-${current * 100}%)` }"
        >
          <div
            v-for="(slide, index) in slides"
            :key="index"
            class="hero__slide"
          >
            <!-- Mobile coupon layout -->
            <div class="hero__coupon hero__coupon--mobile" @click="$emit('download')">
              <div class="hero__coupon-left">
                <span class="hero__clip">📎</span>
                <div>
                  <p class="hero__coupon-title">{{ slide.title }}</p>
                  <p class="hero__coupon-sub">NEW USER OFFER</p>
                </div>
              </div>
              <div class="hero__coupon-divider" />
              <div class="hero__coupon-right">
                <p class="hero__coupon-label">最高抵扣</p>
                <p class="hero__coupon-amount">¥{{ slide.amount }}</p>
              </div>
              <span class="hero__new-badge">NEW</span>
            </div>

            <!-- Desktop layout -->
            <div class="hero__desktop" @click="$emit('download')">
              <div class="hero__content">
                <span class="hero__badge">{{ slide.badge }}</span>
                <h1 class="hero__title">{{ slide.title }}</h1>
                <p class="hero__subtitle">{{ slide.subtitle }}</p>
                <button class="btn-primary btn-primary--lg" @click="$emit('download')">
                  {{ slide.cta }} →
                </button>
              </div>
              <div class="hero__graphic">
                <div class="hero__circle">
                  <p class="hero__circle-label">最高抵扣</p>
                  <p class="hero__circle-amount">¥{{ slide.amount }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="hero__dots">
          <button
            v-for="(_, index) in slides"
            :key="index"
            class="hero__dot"
            :class="{ 'hero__dot--active': index === current }"
            @click="goTo(index)"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'HeroBanner',
  props: {
    slides: {
      type: Array,
      required: true,
    },
  },
  emits: ['download'],
  data() {
    return {
      current: 0,
      timer: null,
    }
  },
  mounted() {
    this.timer = setInterval(() => {
      this.current = (this.current + 1) % this.slides.length
    }, 5000)
  },
  beforeUnmount() {
    clearInterval(this.timer)
  },
  methods: {
    goTo(index) {
      this.current = index
    },
  },
}
</script>

<style scoped>
.hero {
  padding: 16px 0;
}

.hero__slider {
  position: relative;
  overflow: hidden;
  border-radius: var(--radius-xl);
}

.hero__track {
  display: flex;
  transition: transform 0.5s ease;
}

.hero__slide {
  min-width: 100%;
  flex-shrink: 0;
}

/* Mobile coupon */
.hero__coupon {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--color-bg-light);
  border-radius: var(--radius-lg);
  padding: 20px 16px;
  box-shadow: var(--shadow-card);
  overflow: hidden;
}

.hero__coupon--mobile {
  display: flex;
  cursor: pointer;
}

.hero__coupon-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.hero__clip {
  font-size: 20px;
}

.hero__coupon-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--color-text);
}

.hero__coupon-sub {
  font-size: 10px;
  color: var(--color-text-light);
  letter-spacing: 1px;
  margin-top: 2px;
}

.hero__coupon-divider {
  width: 1px;
  height: 60px;
  border-left: 2px dashed var(--color-border);
  margin: 0 12px;
  flex-shrink: 0;
}

.hero__coupon-right {
  text-align: center;
  flex-shrink: 0;
}

.hero__coupon-label {
  font-size: 11px;
  color: var(--color-text-secondary);
}

.hero__coupon-amount {
  font-size: 28px;
  font-weight: 800;
  color: var(--color-accent);
  line-height: 1.2;
}

.hero__new-badge {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: var(--color-accent);
  color: var(--color-white);
  font-size: 10px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotate(-12deg);
}

/* Desktop layout - hidden on mobile */
.hero__desktop {
  display: none;
}

.hero__dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
}

.hero__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ccc;
  border: none;
  padding: 0;
  transition: background 0.3s, transform 0.3s;
}

.hero__dot--active {
  background: var(--color-accent);
  transform: scale(1.2);
}

@media (min-width: 768px) {
  .hero {
    padding: 24px 0;
  }

  .hero__coupon--mobile {
    display: none;
  }

  .hero__desktop {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: var(--color-bg-light);
    border-radius: var(--radius-xl);
    padding: 48px 56px;
    box-shadow: var(--shadow-card);
    gap: 40px;
    min-height: 280px;
    cursor: pointer;
  }

  .hero__content {
    flex: 1;
  }

  .hero__badge {
    display: inline-block;
    background: var(--color-accent);
    color: var(--color-white);
    font-size: 12px;
    font-weight: 600;
    padding: 4px 12px;
    border-radius: 12px;
    margin-bottom: 16px;
  }

  .hero__title {
    font-size: 32px;
    font-weight: 800;
    color: var(--color-text);
    margin-bottom: 12px;
    line-height: 1.3;
  }

  .hero__subtitle {
    font-size: 15px;
    color: var(--color-text-secondary);
    margin-bottom: 24px;
    max-width: 400px;
  }

  .hero__graphic {
    flex-shrink: 0;
  }

  .hero__circle {
    width: 180px;
    height: 180px;
    border-radius: 50%;
    background: rgba(230, 126, 34, 0.12);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 3px solid rgba(230, 126, 34, 0.25);
  }

  .hero__circle-label {
    font-size: 14px;
    color: var(--color-text-secondary);
    margin-bottom: 4px;
  }

  .hero__circle-amount {
    font-size: 48px;
    font-weight: 800;
    color: var(--color-accent);
    line-height: 1;
  }

  .hero__dots {
    position: absolute;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    margin-top: 0;
  }
}
</style>
